import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  OutlinedInput,
  InputAdornment,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Autocomplete
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar } from 'recharts';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { ProductMeasurement, Product, LambdaGroup } from '../types';
import { measurementsApi, lambdaGroupsApi, productsApi } from '../services/api';
import { UserRoleContext } from './Layout';
import { Search as SearchIcon } from '@mui/icons-material';

const Reports: React.FC = () => {
  const { role } = React.useContext(UserRoleContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [measurements, setMeasurements] = useState<ProductMeasurement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [lambdaGroups, setLambdaGroups] = useState<LambdaGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<{
    lambdaGroups: Array<{ name: string; value: number }>;
    products: Array<{ name: string; value: number }>;
    thicknesses: Array<{ name: string; value: number }>;
    monthlyProducts: Array<{ name: string; value: number }>;
    weeklyProducts: Array<{ name: string; value: number }>;
    dailyProducts: Array<{ name: string; value: number }>;
    monthlyGroups: Array<{ name: string; value: number }>;
    weeklyGroups: Array<{ name: string; value: number }>;
    dailyGroups: Array<{ name: string; value: number }>;
  }>({
    lambdaGroups: [],
    products: [],
    thicknesses: [],
    monthlyProducts: [],
    weeklyProducts: [],
    dailyProducts: [],
    monthlyGroups: [],
    weeklyGroups: [],
    dailyGroups: []
  });

  // Nove state varijable za kontrole
  const [selectedLambdaGroups, setSelectedLambdaGroups] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedReports, setSelectedReports] = useState<string[]>(['basic']);
  const [searchLambdaGroups, setSearchLambdaGroups] = useState('');
  const [searchProducts, setSearchProducts] = useState('');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  
  // Novi state za tip charta i dodatne filtere
  const [chartType, setChartType] = useState<'pie' | 'line'>('pie');
  const [excludeRejected, setExcludeRejected] = useState(false);
  const [excludeVerified, setExcludeVerified] = useState(false);
  const [excludeDuplicates, setExcludeDuplicates] = useState(false);

  // Boje za chartove
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B'];

  // Nove funkcije za izračunavanje statistika
  const calculateLambdaGroupDeviations = (measurements: ProductMeasurement[]) => {
    const groupStats: { [key: string]: { good: number; bad: number; total: number } } = {};
    
    measurements.forEach(m => {
      const product = products.find(p => p.name === m.productType);
      if (!product) return;
      
      const groupName = product.lambdaGroup;
      if (!groupStats[groupName]) {
        groupStats[groupName] = { good: 0, bad: 0, total: 0 };
      }
      
      groupStats[groupName].total++;
      
      // Proveri da li je lambda vrednost u dozvoljenim granicama
      if (m.lambdaVrednost !== undefined && m.lambdaVrednost !== null) {
        // Ovde možeš dodati logiku za proveru granica prema lambda grupi
        const isGood = m.lambdaVrednost <= 0.040; // Primer granice
        if (isGood) {
          groupStats[groupName].good++;
        } else {
          groupStats[groupName].bad++;
        }
      }
    });
    
    return Object.entries(groupStats).map(([group, stats]) => ({
      name: group,
      good: stats.good,
      bad: stats.bad,
      total: stats.total,
      goodPercentage: stats.total > 0 ? (stats.good / stats.total) * 100 : 0,
      badPercentage: stats.total > 0 ? (stats.bad / stats.total) * 100 : 0
    }));
  };

  const calculateThicknessDeviations = (measurements: ProductMeasurement[]) => {
    const deviations: { [key: string]: { [product: string]: number[] } } = {};
    
    measurements.forEach(m => {
      const product = products.find(p => p.name === m.productType);
      if (!product) return;
      
      const groupName = product.lambdaGroup;
      if (!deviations[groupName]) {
        deviations[groupName] = {};
      }
      if (!deviations[groupName][product.name]) {
        deviations[groupName][product.name] = [];
      }
      
      // Izračunaj odstupanja debljine (primer sa avgThickness)
      if (m.avgThickness !== undefined && m.avgThickness !== null) {
        const targetThickness = product.thickness || 0;
        const deviation = Math.abs(Number(m.avgThickness) - targetThickness);
        deviations[groupName][product.name].push(deviation);
      }
    });
    
    return Object.entries(deviations).map(([group, products]) => ({
      name: group,
      products: Object.entries(products).map(([product, deviations]) => ({
        name: product,
        avgDeviation: deviations.length > 0 ? deviations.reduce((a, b) => a + b, 0) / deviations.length : 0,
        maxDeviation: deviations.length > 0 ? Math.max(...deviations) : 0,
        count: deviations.length
      }))
    }));
  };

  const calculateDryMatterStats = (measurements: ProductMeasurement[]) => {
    const productStats: { [key: string]: number[] } = {};
    
    measurements.forEach(m => {
      if (!productStats[m.productType]) {
        productStats[m.productType] = [];
      }
      
      // Izračunaj suvu materiju (primer sa procenatSM)
      if (m.procenatSM !== undefined && m.procenatSM !== null) {
        productStats[m.productType].push(Number(m.procenatSM));
      }
    });
    
    return Object.entries(productStats).map(([product, values]) => ({
      name: product,
      avg: values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0,
      min: values.length > 0 ? Math.min(...values) : 0,
      max: values.length > 0 ? Math.max(...values) : 0,
      count: values.length
    }));
  };

  // Funkcije za filtriranje
  const getFilteredMeasurements = () => {
    let filtered = measurements;

    // Filter po lambda grupama
    if (selectedLambdaGroups.length > 0) {
      filtered = filtered.filter(m => {
        const product = products.find(p => p.name === m.productType);
        return product && selectedLambdaGroups.includes(product.lambdaGroup);
      });
    }

    // Filter po proizvodima
    if (selectedProducts.length > 0) {
      filtered = filtered.filter(m => selectedProducts.includes(m.productType));
    }

    // Filter po datumu
    if (dateFrom) {
      filtered = filtered.filter(m => {
        const measurementDate = dayjs(m.datumProizvodnje);
        return measurementDate.isAfter(dateFrom) || measurementDate.isSame(dateFrom, 'day');
      });
    }

    if (dateTo) {
      filtered = filtered.filter(m => {
        const measurementDate = dayjs(m.datumProizvodnje);
        return measurementDate.isBefore(dateTo) || measurementDate.isSame(dateTo, 'day');
      });
    }

    // Dodatni filteri
    if (excludeRejected) {
      filtered = filtered.filter(m => !m.notVerified);
    }

    if (excludeVerified) {
      filtered = filtered.filter(m => !m.verified);
    }

    if (excludeDuplicates) {
      filtered = filtered.filter(m => !m.naknadno);
    }

    return filtered;
  };

  // Funkcije za kontrole
  const handleLambdaGroupChange = (event: any) => {
    const value = event.target.value;
    setSelectedLambdaGroups(typeof value === 'string' ? value.split(',') : value);
  };

  const handleProductChange = (event: any) => {
    const value = event.target.value;
    setSelectedProducts(typeof value === 'string' ? value.split(',') : value);
  };

  const handleReportChange = (reportType: string) => {
    setSelectedReports(prev => 
      prev.includes(reportType) 
        ? prev.filter(r => r !== reportType)
        : [...prev, reportType]
    );
  };

  // Funkcija za reset filtera
  const resetFilters = () => {
    setSelectedLambdaGroups([]);
    setSelectedProducts([]);
    setSelectedReports(['basic']);
    setSearchLambdaGroups('');
    setSearchProducts('');
    setDateFrom(null);
    setDateTo(null);
    setChartType('pie');
    setExcludeRejected(false);
    setExcludeVerified(false);
    setExcludeDuplicates(false);
  };

  // Funkcije za search
  const getFilteredLambdaGroups = () => {
    return lambdaGroups.filter(group => 
      group.name.toLowerCase().includes(searchLambdaGroups.toLowerCase())
    ).slice(0, 10);
  };

  const getFilteredProducts = () => {
    return products.filter(product => 
      product.name.toLowerCase().includes(searchProducts.toLowerCase())
    ).slice(0, 10);
  };

  // Funkcija za renderovanje chartova
  const renderChart = (
    data: Array<{ name: string; value: number; lambdaGroup?: string }>,
    title: string,
    allowLineChart: boolean = true,
    showLegend: boolean = true
  ) => {
    if (chartType === 'pie' || !allowLineChart) {
        return (
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 320}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={isMobile ? 45 : 80}
                outerRadius={isMobile ? 85 : 135}
                paddingAngle={1} // manji razmak između segmenata da se bolje vide
                minAngle={3} // minimalna vidljiva širina segmenta (i za vrlo male vrednosti)
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [`${value} merenja`, name]}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '6px 8px' : '8px 12px',
                }}
              />
              {!isMobile && showLegend && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        );
    } else {
      // Za line chart, pripremi podatke po lambda grupama
      const uniqueLambdaGroups = Array.from(new Set(data.map(item => item.lambdaGroup).filter(Boolean)));
      
      const lineData = data.map(item => {
        const result: any = { name: item.name };
        
        // Dodaj vrednost za svaku lambda grupu
        if (uniqueLambdaGroups.length > 0) {
          uniqueLambdaGroups.forEach((group) => {
            if (group && item.lambdaGroup === group) {
              result[group] = item.value;
            } else if (group) {
              result[group] = 0; // Nula za grupe koje nemaju podatke za ovaj proizvod
            }
          });
        } else {
          result.value = item.value;
        }
        
        return result;
      });

      return (
        <ResponsiveContainer width="100%" height={isMobile ? 200 : 320}>
          <LineChart data={lineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip 
              formatter={(value, name) => [`${value} merenja`, name]}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '6px 8px' : '8px 12px',
              }}
            />
            {!isMobile && showLegend && <Legend />}
            {uniqueLambdaGroups.length > 0 ? (
              uniqueLambdaGroups.map((group, index) => (
                group && (
                  <Line 
                    key={group}
                    type="monotone" 
                    dataKey={group} 
                    stroke={COLORS[index % COLORS.length]} 
                    strokeWidth={2}
                    name={group}
                  />
                )
              ))
            ) : (
              <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
            )}
          </LineChart>
        </ResponsiveContainer>
      );
    }
  };

  // Funkcija za renderovanje pie chart-a (samo za odstupanja)
  const renderPieChart = (data: Array<{ name: string; value: number }>, customColors?: string[]) => {
    return (
      <ResponsiveContainer width="100%" height={isMobile ? 200 : 320}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={isMobile ? 45 : 80}
            outerRadius={isMobile ? 85 : 135}
            paddingAngle={1}
            minAngle={3}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={customColors ? customColors[index % customColors.length] : COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            formatter={(value, name) => [`${value} merenja`, name]}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: isMobile ? '12px' : '14px',
              padding: isMobile ? '6px 8px' : '8px 12px',
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Funkcija za renderovanje bar chart-a (za lambda odstupanja)
  const renderBarChart = (data: Array<{ name: string; good: number; bad: number }>) => {
    return (
      <ResponsiveContainer width="100%" height={isMobile ? 200 : 320}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip 
            formatter={(value, name) => [`${value} merenja`, name]}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: isMobile ? '12px' : '14px',
              padding: isMobile ? '6px 8px' : '8px 12px',
            }}
          />
          {!isMobile && <Legend />}
          <Bar dataKey="good" fill="#00C49F" name="Dobra" />
          <Bar dataKey="bad" fill="#FF8042" name="Loša" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [measurementsData, lambdaGroupsData, productsData] = await Promise.all([
        measurementsApi.getAll(),
        lambdaGroupsApi.getAll(),
        productsApi.getAll()
      ]);
      
      setMeasurements(measurementsData);
      setLambdaGroups(lambdaGroupsData);
      setProducts(productsData);
    } catch (err) {
      setError('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  // Pripremi podatke za chartove
  const prepareChartData = (measurements: ProductMeasurement[]) => {
    if (!measurements || !products || !lambdaGroups) {
      return;
    }
    
    const now = dayjs();
    const oneMonthAgo = now.subtract(1, 'month');
    const oneWeekAgo = now.subtract(1, 'week');
    const oneDayAgo = now.subtract(1, 'day');

    // Lambda grupe chart - prikaži ukupan broj merenja za sve grupe
    const lambdaGroupsData = [{
      name: 'Sve grupe',
      value: measurements.length
    }];

    // Proizvodi chart - pripremi podatke po lambda grupama za line chart
    const productsData = measurements.reduce((acc: Array<{ name: string; value: number; lambdaGroup?: string }>, measurement) => {
      const product = products.find(p => p.name === measurement.productType);
      if (product) {
        const existing = acc.find(item => item.name === product.name);
        if (existing) {
          existing.value += 1;
        } else {
          acc.push({ 
            name: product.name, 
            value: 1,
            lambdaGroup: product.lambdaGroup 
          });
        }
      }
      return acc;
    }, []);

    // Debljina chart
    const thicknessesData = measurements.reduce((acc: Array<{ name: string; value: number }>, measurement) => {
      const product = products.find(p => p.name === measurement.productType);
      if (product) {
        const thickness = `${product.thickness} mm`;
        const existing = acc.find(item => item.name === thickness);
        if (existing) {
          existing.value += 1;
        } else {
          acc.push({ name: thickness, value: 1 });
        }
      }
      return acc;
    }, []);

    // Mesecni proizvodi
    const monthlyProductsData = measurements
      .filter(m => dayjs(m.datumProizvodnje).isAfter(oneMonthAgo))
      .reduce((acc: Array<{ name: string; value: number; lambdaGroup?: string }>, measurement) => {
        const product = products.find(p => p.name === measurement.productType);
        if (product) {
          const existing = acc.find(item => item.name === product.name);
          if (existing) {
            existing.value += 1;
          } else {
            acc.push({ 
              name: product.name, 
              value: 1,
              lambdaGroup: product.lambdaGroup 
            });
          }
        }
        return acc;
      }, []);

    // Nedeljni proizvodi
    const weeklyProductsData = measurements
      .filter(m => dayjs(m.datumProizvodnje).isAfter(oneWeekAgo))
      .reduce((acc: Array<{ name: string; value: number; lambdaGroup?: string }>, measurement) => {
        const product = products.find(p => p.name === measurement.productType);
        if (product) {
          const existing = acc.find(item => item.name === product.name);
          if (existing) {
            existing.value += 1;
          } else {
            acc.push({ 
              name: product.name, 
              value: 1,
              lambdaGroup: product.lambdaGroup 
            });
          }
        }
        return acc;
      }, []);

    // Mesecne grupe
    const monthlyGroupsData = measurements
      .filter(m => dayjs(m.datumProizvodnje).isAfter(oneMonthAgo))
      .reduce((acc: Array<{ name: string; value: number }>, measurement) => {
        const product = products.find(p => p.name === measurement.productType);
        if (product) {
          const existing = acc.find(item => item.name === product.lambdaGroup);
          if (existing) {
            existing.value += 1;
          } else {
            acc.push({ name: product.lambdaGroup, value: 1 });
          }
        }
        return acc;
      }, []);

    // Nedeljne grupe
    const weeklyGroupsData = measurements
      .filter(m => dayjs(m.datumProizvodnje).isAfter(oneWeekAgo))
      .reduce((acc: Array<{ name: string; value: number }>, measurement) => {
        const product = products.find(p => p.name === measurement.productType);
        if (product) {
          const existing = acc.find(item => item.name === product.lambdaGroup);
          if (existing) {
            existing.value += 1;
          } else {
            acc.push({ name: product.lambdaGroup, value: 1 });
          }
        }
        return acc;
      }, []);

    setChartData({
      lambdaGroups: lambdaGroupsData,
      products: productsData,
      thicknesses: thicknessesData,
      monthlyProducts: monthlyProductsData,
      weeklyProducts: weeklyProductsData,
      monthlyGroups: monthlyGroupsData,
      weeklyGroups: weeklyGroupsData,
      dailyProducts: [],
      dailyGroups: []
    });
  };

  // Pripremi chart podatke kada se promene merenja ili proizvodi
  useEffect(() => {
    if (measurements && measurements.length > 0 && 
        products && products.length > 0 && 
        lambdaGroups && lambdaGroups.length > 0) {
      prepareChartData(getFilteredMeasurements());
    }
  }, [measurements, products, lambdaGroups, selectedLambdaGroups, selectedProducts, dateFrom, dateTo, excludeRejected, excludeVerified, excludeDuplicates]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (role === 'tehnolog') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Samo admin, master admin ili direktor može da pristupi ovoj stranici.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: isMobile ? 1 : 4, 
      px: isMobile ? 0.5 : 3,
      '@media (max-width: 480px)': {
        py: 0.5,
        px: 0.25,
        maxWidth: '100vw',
        overflow: 'hidden',
      }
    }}>
      <Box sx={{ mb: isMobile ? 1 : 4 }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontSize: isMobile ? '1.5rem' : '2.125rem',
            '@media (max-width: 480px)': {
              fontSize: '1.25rem',
              mb: 1,
            }
          }}
        >
          Izveštaji i statistika
        </Typography>
        <Typography 
          variant="subtitle1" 
          color="text.secondary" 
          sx={{ 
            fontSize: isMobile ? '0.875rem' : '1rem',
            '@media (max-width: 480px)': {
              fontSize: '0.75rem',
            }
          }}
        >
          Pregled statistike tehničkih merenja proizvoda
        </Typography>
      </Box>

      {/* Objedinjene kontrole za filtriranje */}
      <Paper sx={{ 
        p: isMobile ? 1.5 : 3, 
        mb: isMobile ? 2 : 3,
        '@media (max-width: 480px)': {
          p: 1,
          mb: 1.5,
        }
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h6"} 
          gutterBottom 
          sx={{ 
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            '@media (max-width: 480px)': {
              fontSize: '1rem',
              mb: 1,
            }
          }}
        >
          Kontrole za filtriranje
        </Typography>
        
                    <Box sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1.5 : 3, 
              flexWrap: 'wrap', 
              mb: isMobile ? 2 : 3,
              width: '100%',
              overflow: 'hidden',
              '@media (max-width: 480px)': {
                gap: 1,
                mb: 1.5,
                maxWidth: '100vw',
              }
            }}>
          {/* Lambda grupe */}
          <FormControl sx={{ minWidth: isMobile ? '100%' : 250 }}>
            <InputLabel>Lambda grupe</InputLabel>
            <Select
              multiple
              value={selectedLambdaGroups}
              onChange={handleLambdaGroupChange}
              input={<OutlinedInput label="Lambda grupe" />}
              size={isMobile ? "small" : "medium"}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              <Box sx={{ p: 1 }}>
                <TextField
                  size="small"
                  placeholder="Pretraži lambda grupe..."
                  value={searchLambdaGroups}
                  onChange={(e) => setSearchLambdaGroups(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  sx={{ mb: 1 }}
                />
              </Box>
              {getFilteredLambdaGroups().map((group) => (
                <MenuItem key={group._id} value={group.name}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Proizvodi */}
          <Box sx={{ minWidth: isMobile ? '100%' : 250 }}>
            <Autocomplete
              multiple
              options={products.map(p => p.name)}
              value={selectedProducts}
              onChange={(_, value) => setSelectedProducts(value)}
              disableCloseOnSelect
              getOptionLabel={(option) => option}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proizvodi"
                  placeholder="Počni da kucaš..."
                  size={isMobile ? 'small' : 'medium'}
                />
              )}
            />
          </Box>

          {/* Datum filter */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
              <DatePicker
                label="Od datuma"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                slotProps={{ 
                  textField: { 
                    size: isMobile ? 'small' : 'small',
                    fullWidth: isMobile
                  } 
                }}
              />
              <DatePicker
                label="Do datuma"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                slotProps={{ 
                  textField: { 
                    size: isMobile ? 'small' : 'small',
                    fullWidth: isMobile
                  } 
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {/* Tipovi izveštaja */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
          Izaberi tipove izveštaja:
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 2, 
          flexWrap: 'wrap', 
          mb: 2 
        }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReports.includes('basic')}
                onChange={() => handleReportChange('basic')}
              />
            }
            label="Osnovna statistika"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReports.includes('time')}
                onChange={() => handleReportChange('time')}
              />
            }
            label="Vremenska statistika"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReports.includes('lambda')}
                onChange={() => handleReportChange('lambda')}
              />
            }
            label="Lambda grupa odstupanja"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReports.includes('thickness')}
                onChange={() => handleReportChange('thickness')}
              />
            }
            label="Odstupanja debljine"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedReports.includes('dryMatter')}
                onChange={() => handleReportChange('dryMatter')}
              />
            }
            label="Suva materija"
          />
        </Box>

        {/* Tip charta i dodatni filteri */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
          {/* Tip charta */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Tip charta</InputLabel>
            <Select
              value={chartType}
              onChange={(e) => setChartType(e.target.value as 'pie' | 'line')}
              input={<OutlinedInput label="Tip charta" />}
              size="small"
            >
              <MenuItem value="pie">Pie Chart</MenuItem>
              <MenuItem value="line">Line Chart</MenuItem>
            </Select>
          </FormControl>

          {/* Dodatni filteri */}
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeRejected}
                onChange={(e) => setExcludeRejected(e.target.checked)}
              />
            }
            label="Isključi odbijene"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeVerified}
                onChange={(e) => setExcludeVerified(e.target.checked)}
              />
            }
            label="Isključi verifikovane"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeDuplicates}
                onChange={(e) => setExcludeDuplicates(e.target.checked)}
              />
            }
            label="Isključi duplikate"
          />
        </Box>

        {/* Dugme za reset */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={resetFilters}
            size="small"
          >
            Resetuj filtere
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Chartovi */}
      {selectedReports.includes('basic') && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Osnovna statistika merenja ({getFilteredMeasurements().length} filtriranih merenja)
          </Typography>
          {!measurements || measurements.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              Nema podataka za prikaz chartova
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* Lambda grupe chart */}
              <Box sx={{ 
                flex: '1 1 300px', 
                minWidth: isMobile ? 250 : 300,
                '@media (max-width: 480px)': {
                  minWidth: 200,
                  flex: '1 1 100%',
                }
              }}>
                                  <Paper elevation={0} sx={{ 
                    p: isMobile ? 1 : 2, 
                    pb: isMobile ? 3 : 5, 
                    height: isMobile ? 350 : 420, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between', 
                    boxShadow: 'none',
                    '@media (max-width: 480px)': {
                      p: 0.5,
                      pb: 2,
                      height: 300,
                    }
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      align="center" 
                      gutterBottom
                      sx={{
                        '@media (max-width: 480px)': {
                          fontSize: '0.875rem',
                        }
                      }}
                    >
                      Ukupan broj merenja
                    </Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {chartData.lambdaGroups && chartData.lambdaGroups.length > 0 ? (
                      renderChart(chartData.lambdaGroups, 'Ukupan broj merenja', true, true)
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nema podataka
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Legend />
                    <Typography variant="caption" color="text.secondary" align="center">
                      Ukupno merenja: {chartData.lambdaGroups?.[0]?.value || 0}
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Proizvodi chart */}
              <Box sx={{ 
                flex: '1 1 300px', 
                minWidth: isMobile ? 250 : 300,
                '@media (max-width: 480px)': {
                  minWidth: 200,
                  flex: '1 1 100%',
                }
              }}>
                                  <Paper elevation={0} sx={{ 
                    p: isMobile ? 1 : 2, 
                    pb: isMobile ? 3 : 5, 
                    height: isMobile ? 350 : 420, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between', 
                    boxShadow: 'none',
                    '@media (max-width: 480px)': {
                      p: 0.5,
                      pb: 2,
                      height: 300,
                    }
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      align="center" 
                      gutterBottom
                      sx={{
                        '@media (max-width: 480px)': {
                          fontSize: '0.875rem',
                        }
                      }}
                    >
                      Po proizvodima
                    </Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {chartData.products && chartData.products.length > 0 ? (
                      renderChart(chartData.products, 'Po proizvodima', true, false)
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nema podataka
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Legend />
                    <Typography variant="caption" color="text.secondary" align="center">
                      Podaci: {chartData.products?.length || 0} proizvoda
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Debljina chart */}
              <Box sx={{ 
                flex: '1 1 300px', 
                minWidth: isMobile ? 250 : 300,
                '@media (max-width: 480px)': {
                  minWidth: 200,
                  flex: '1 1 100%',
                }
              }}>
                                  <Paper elevation={0} sx={{ 
                    p: isMobile ? 1 : 2, 
                    pb: isMobile ? 3 : 5, 
                    height: isMobile ? 350 : 420, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between', 
                    boxShadow: 'none',
                    '@media (max-width: 480px)': {
                      p: 0.5,
                      pb: 2,
                      height: 300,
                    }
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      align="center" 
                      gutterBottom
                      sx={{
                        '@media (max-width: 480px)': {
                          fontSize: '0.875rem',
                        }
                      }}
                    >
                      Po debljini
                    </Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {chartData.thicknesses && chartData.thicknesses.length > 0 ? (
                      renderChart(chartData.thicknesses, 'Po debljini', true, false)
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nema podataka
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box>
                    <Legend />
                    <Typography variant="caption" color="text.secondary" align="center">
                      Podaci: {chartData.thicknesses?.length || 0} debljina
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Vremenski chartovi */}
      {selectedReports.includes('time') && measurements && measurements.length > 0 && (
        <Paper sx={{ 
          p: isMobile ? 2 : 3, 
          mb: isMobile ? 2 : 3,
          '@media (max-width: 480px)': {
            p: 1,
            mb: 1.5,
          }
        }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              '@media (max-width: 480px)': {
                fontSize: '1rem',
              }
            }}
          >
            Vremenska statistika
          </Typography>
          
          {/* Prikazujemo sekciju samo ako ima podataka */}
          {(() => {
            const filtered = getFilteredMeasurements();
            const now = dayjs();
            const oneMonthAgo = now.subtract(1, 'month');
            const oneWeekAgo = now.subtract(1, 'week');

            // Mesecni proizvodi
            const monthlyProductsData = filtered
              .filter(m => dayjs(m.datumProizvodnje).isAfter(oneMonthAgo))
              .reduce((acc: Array<{ name: string; value: number }>, measurement) => {
                const product = products.find(p => p.name === measurement.productType);
                if (product) {
                  const existing = acc.find(item => item.name === product.name);
                  if (existing) {
                    existing.value += 1;
                  } else {
                    acc.push({ name: product.name, value: 1 });
                  }
                }
                return acc;
              }, [])
              .filter(item => selectedProducts.length === 0 || selectedProducts.includes(item.name));

            // Nedeljni proizvodi
            const weeklyProductsData = filtered
              .filter(m => dayjs(m.datumProizvodnje).isAfter(oneWeekAgo))
              .reduce((acc: Array<{ name: string; value: number }>, measurement) => {
                const product = products.find(p => p.name === measurement.productType);
                if (product) {
                  const existing = acc.find(item => item.name === product.name);
                  if (existing) {
                    existing.value += 1;
                  } else {
                    acc.push({ name: product.name, value: 1 });
                  }
                }
                return acc;
              }, [])
              .filter(item => selectedProducts.length === 0 || selectedProducts.includes(item.name));

            // Mesecni grupe
            const monthlyGroupsData = selectedLambdaGroups.length === 0 ? 
              [{
                name: 'Sve grupe',
                value: filtered.filter(m => dayjs(m.datumProizvodnje).isAfter(oneMonthAgo)).length
              }] :
              selectedLambdaGroups.map(groupName => ({
                name: groupName,
                value: filtered.filter(m => {
                  const product = products.find(p => p.name === m.productType);
                  return product && product.lambdaGroup === groupName && dayjs(m.datumProizvodnje).isAfter(oneMonthAgo);
                }).length
              })).filter(group => group.value > 0);

            // Nedeljni grupe
            const weeklyGroupsData = selectedLambdaGroups.length === 0 ? 
              [{
                name: 'Sve grupe',
                value: filtered.filter(m => dayjs(m.datumProizvodnje).isAfter(oneWeekAgo)).length
              }] :
              selectedLambdaGroups.map(groupName => ({
                name: groupName,
                value: filtered.filter(m => {
                  const product = products.find(p => p.name === m.productType);
                  return product && product.lambdaGroup === groupName && dayjs(m.datumProizvodnje).isAfter(oneWeekAgo);
                }).length
              })).filter(group => group.value > 0);

            const hasMonthly = filtered.length > 0 && (monthlyProductsData.length > 0 || monthlyGroupsData.length > 0);
            const hasWeekly = filtered.length > 0 && (weeklyProductsData.length > 0 || weeklyGroupsData.length > 0);
            
            return <>
              {hasMonthly && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                    Poslednji mesec
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                    {/* Mesecni proizvodi */}
                    {monthlyProductsData.length > 0 && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                        <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                          <Typography variant="subtitle2" align="center" gutterBottom>
                            Proizvodi (mesec)
                          </Typography>
                          {renderChart(monthlyProductsData, 'Proizvodi (mesec)', true, false)}
                        </Paper>
                      </Box>
                    )}

                    {/* Mesecne grupe */}
                    {monthlyGroupsData.length > 0 && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                        <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                          <Typography variant="subtitle2" align="center" gutterBottom>
                            Grupe (mesec)
                          </Typography>
                          {renderChart(monthlyGroupsData, 'Grupe (mesec)', true, false)}
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </>
              )}
              {hasWeekly && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                    Poslednja nedelja
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                    {/* Nedeljni proizvodi */}
                    {weeklyProductsData.length > 0 && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                        <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                          <Typography variant="subtitle2" align="center" gutterBottom>
                            Proizvodi (nedelja)
                          </Typography>
                          {renderChart(weeklyProductsData, 'Proizvodi (nedelja)', true, false)}
                        </Paper>
                      </Box>
                    )}

                    {/* Nedeljne grupe */}
                    {weeklyGroupsData.length > 0 && (
                      <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                        <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                          <Typography variant="subtitle2" align="center" gutterBottom>
                            Grupe (nedelja)
                          </Typography>
                          {renderChart(weeklyGroupsData, 'Grupe (nedelja)', true, false)}
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </>;
          })()}
        </Paper>
      )}

      {/* Novi izveštaji */}
      {measurements && measurements.length > 0 && 
       (selectedReports.includes('lambda') || selectedReports.includes('thickness') || selectedReports.includes('dryMatter')) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Detaljna analiza ({getFilteredMeasurements().length} filtriranih merenja)
          </Typography>
          
          {/* Lambda grupa odstupanja */}
          {selectedReports.includes('lambda') && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                1. Lambda grupa - procentualna odstupanja
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                {calculateLambdaGroupDeviations(getFilteredMeasurements())
                  .filter(group => selectedLambdaGroups.length === 0 || selectedLambdaGroups.includes(group.name)) // Prikaži samo izabrane lambda grupe
                  .map((group, index) => (
                  <Box key={group.name} sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                      <Typography variant="subtitle2" align="center" gutterBottom>
                        {group.name}
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {renderBarChart([
                          { name: group.name, good: group.good, bad: group.bad }
                        ])}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" align="center">
                          Ukupno: {group.total} | Dobra: {group.goodPercentage.toFixed(1)}% | Loša: {group.badPercentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Odstupanja debljine */}
          {selectedReports.includes('thickness') && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                2. Odstupanja debljine po lambda grupama
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                {calculateThicknessDeviations(getFilteredMeasurements())
                  .filter(group => selectedLambdaGroups.length === 0 || selectedLambdaGroups.includes(group.name)) // Prikaži samo izabrane lambda grupe
                  .map((group, index) => (
                  <Box key={group.name} sx={{ flex: '1 1 400px', minWidth: 400 }}>
                    <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                      <Typography variant="subtitle2" align="center" gutterBottom>
                        {group.name} - Prosečna odstupanja
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {renderPieChart(group.products
                          .filter(product => selectedProducts.length === 0 || selectedProducts.includes(product.name))
                          .map(product => ({
                          name: product.name,
                          value: product.avgDeviation
                        })))}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" align="center">
                          Proizvodi: {group.products.filter(p => selectedProducts.length === 0 || selectedProducts.includes(p.name)).length} | Merenja: {group.products.filter(p => selectedProducts.length === 0 || selectedProducts.includes(p.name)).reduce((sum, p) => sum + p.count, 0)}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* Suva materija */}
          {selectedReports.includes('dryMatter') && (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, mb: 2 }}>
                3. Suva materija po proizvodu
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {calculateDryMatterStats(getFilteredMeasurements())
                  .filter(product => selectedProducts.length === 0 || selectedProducts.includes(product.name)) // Prikaži samo izabrane proizvode
                  .map((product, index) => (
                  <Box key={product.name} sx={{ flex: '1 1 300px', minWidth: 300 }}>
                    <Paper elevation={0} sx={{ p: 2, pb: 5, height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: 'none' }}>
                      <Typography variant="subtitle2" align="center" gutterBottom>
                        {product.name}
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {renderChart([
                          { name: 'Prosečna', value: product.avg },
                          { name: 'Min', value: product.min },
                          { name: 'Max', value: product.max }
                        ], product.name, true, false)}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" align="center">
                          Merenja: {product.count} | Prosečna: {product.avg.toFixed(2)}% | Min: {product.min.toFixed(2)}% | Max: {product.max.toFixed(2)}%
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default Reports; 